import { useState, useMemo } from 'react';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import {
  logsAuditoria,
  modulosAuditoria,
  getAcaoConfig,
  getModuloLabel,
  getModuloColor,
  type LogAuditoria,
} from '@/mocks/backoffice/auditoria';

export default function BackofficeAuditoriaPage() {
  const [busca, setBusca] = useState('');
  const [filtroModulo, setFiltroModulo] = useState<string>('todos');
  const [filtroAcao, setFiltroAcao] = useState<string>('todos');
  const [filtroUsuario, setFiltroUsuario] = useState<string>('todos');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const usuariosUnicos = useMemo(() => {
    const users = [...new Set(logsAuditoria.map((l) => l.usuario))];
    return users.sort();
  }, []);

  const logsFiltrados = useMemo(() => {
    return logsAuditoria.filter((log) => {
      const matchBusca =
        !busca ||
        log.acao.toLowerCase().includes(busca.toLowerCase()) ||
        log.detalhes.toLowerCase().includes(busca.toLowerCase()) ||
        log.usuario.toLowerCase().includes(busca.toLowerCase()) ||
        log.entidadeId?.toLowerCase().includes(busca.toLowerCase());
      const matchModulo = filtroModulo === 'todos' || log.modulo === filtroModulo;
      const matchAcao = filtroAcao === 'todos' || log.acao === filtroAcao;
      const matchUsuario = filtroUsuario === 'todos' || log.usuario === filtroUsuario;

      let matchData = true;
      if (dataInicio) {
        matchData = matchData && new Date(log.data) >= new Date(dataInicio + 'T00:00:00');
      }
      if (dataFim) {
        matchData = matchData && new Date(log.data) <= new Date(dataFim + 'T23:59:59');
      }

      return matchBusca && matchModulo && matchAcao && matchUsuario && matchData;
    });
  }, [busca, filtroModulo, filtroAcao, filtroUsuario, dataInicio, dataFim]);

  const stats = useMemo(() => {
    const total = logsFiltrados.length;
    const hoje = new Date().toISOString().split('T')[0];
    const hojeCount = logsFiltrados.filter((l) => l.data.startsWith(hoje)).length;
    const acoesUnicas = new Set(logsFiltrados.map((l) => l.acao)).size;
    const usuariosAtivos = new Set(logsFiltrados.map((l) => l.usuario)).size;
    return { total, hojeCount, acoesUnicas, usuariosAtivos };
  }, [logsFiltrados]);

  const acoesPorModulo = useMemo(() => {
    const map = new Map<string, number>();
    logsFiltrados.forEach((l) => {
      map.set(l.modulo, (map.get(l.modulo) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([modulo, count]) => ({ modulo, count, label: getModuloLabel(modulo) }))
      .sort((a, b) => b.count - a.count);
  }, [logsFiltrados]);

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-800">Auditoria de Ações</h1>
          <p className="text-sm text-gray-500 mt-1">Rastreamento de todas as ações realizadas no backoffice</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[
            { label: 'Total de Logs', value: stats.total, icon: 'ri-file-list-line', color: 'text-gray-700', bg: 'bg-gray-50' },
            { label: 'Hoje', value: stats.hojeCount, icon: 'ri-calendar-check-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Ações Diferentes', value: stats.acoesUnicas, icon: 'ri-stack-line', color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Usuários Ativos', value: stats.usuariosAtivos, icon: 'ri-user-line', color: 'text-purple-600', bg: 'bg-purple-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${s.icon} ${s.color} text-lg`}></i>
                  </div>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Resumo por Módulo */}
        {acoesPorModulo.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {acoesPorModulo.map((m) => (
              <div
                key={m.modulo}
                className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${getModuloColor(m.modulo)}`}
              >
                <span>{m.label}</span>
                <span className="opacity-70">({m.count})</span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm"></i>
              </div>
              <input
                type="text"
                placeholder="Buscar por ação, usuário, detalhes ou ID..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select
              value={filtroModulo}
              onChange={(e) => setFiltroModulo(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
            >
              {modulosAuditoria.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
            <select
              value={filtroAcao}
              onChange={(e) => setFiltroAcao(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
            >
              <option value="todos">Todas as ações</option>
              {[
                'aprovar_operacao',
                'rejeitar_operacao',
                'criar_operacao_manual',
                'alterar_status_chamado',
                'atribuir_chamado',
                'adicionar_resposta',
                'enviar_comunicado',
                'alterar_configuracao',
                'ativar_empresa',
                'suspender_empresa',
                'editar_empresa',
                'login',
                'logout',
                'login_falha',
              ].map((a) => (
                <option key={a} value={a}>{getAcaoConfig(a).label}</option>
              ))}
            </select>
            <select
              value={filtroUsuario}
              onChange={(e) => setFiltroUsuario(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-white"
            >
              <option value="todos">Todos os usuários</option>
              {usuariosUnicos.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-3 pt-3 border-t border-gray-50">
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500">De:</label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-500">Até:</label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            {(dataInicio || dataFim || filtroModulo !== 'todos' || filtroAcao !== 'todos' || filtroUsuario !== 'todos' || busca) && (
              <button
                onClick={() => {
                  setBusca('');
                  setFiltroModulo('todos');
                  setFiltroAcao('todos');
                  setFiltroUsuario('todos');
                  setDataInicio('');
                  setDataFim('');
                }}
                className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-200 cursor-pointer"
              >
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-close-line text-xs"></i>
                  </div>
                  <span>Limpar filtros</span>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-12"></th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Ação</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Módulo</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Detalhes</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Usuário</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">IP</th>
                </tr>
              </thead>
              <tbody>
                {logsFiltrados.map((log) => {
                  const acaoConfig = getAcaoConfig(log.acao);
                  return (
                    <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-all duration-200">
                      <td className="px-4 py-3">
                        <div className={`w-8 h-8 rounded-lg ${acaoConfig.bg} flex items-center justify-center`}>
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${acaoConfig.icon} ${acaoConfig.color} text-xs`}></i>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold text-gray-800">{acaoConfig.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${getModuloColor(log.modulo)}`}>
                          {getModuloLabel(log.modulo)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-600 max-w-[300px] truncate" title={log.detalhes}>
                          {log.detalhes}
                        </p>
                        {log.entidadeId && (
                          <p className="text-[10px] text-gray-400 mt-0.5 font-mono">{log.entidadeId}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-xs font-bold text-gray-700">{log.usuario}</p>
                          <p className="text-[10px] text-gray-400 capitalize">{log.usuarioRole}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(log.data).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 font-mono">{log.ip || '-'}</td>
                    </tr>
                  );
                })}
                {logsFiltrados.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                      Nenhum registro encontrado com os filtros selecionados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-500">
              Mostrando <span className="font-bold">{logsFiltrados.length}</span> de{' '}
              <span className="font-bold">{logsAuditoria.length}</span> registros
            </p>
          </div>
        </div>
      </div>
    </BackofficeLayout>
  );
}